import { lazy, Suspense, useEffect, useState } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
// Sprint 2: legacy trackPageView removed — pageviews now driven by
// AnalyticsProvider → useTrackPageView → analytics.page().
import { AuthProvider } from "@/hooks/useAuth";
import { AppProvider } from "@/hooks/useApp";
import Layout from "@/components/Layout";
import Home from "@/pages/Home";
import RouteLoader from "@/components/RouteLoader";
import { AnalyticsProvider } from "@/components/AnalyticsProvider";
import { AnalyticsErrorBoundary } from "@/components/AnalyticsErrorBoundary";
import { ConsentBanner } from "@/components/ConsentBanner";
import TabSyncBridge from "@/components/system/TabSyncBridge";

// Defer non-critical UI (toasters + Radix tooltip provider) so they don't ship
// in the initial chunk. They render nothing on first paint anyway.
const DeferredOverlays = lazy(() => import("@/components/DeferredOverlays"));

// Lazy-loaded routes to reduce initial JS bundle and defer third-party scripts (e.g. Stripe).
const Products = lazy(() => import("@/pages/Products"));
const Services = lazy(() => import("@/pages/Services"));
const ProductDetail = lazy(() => import("@/pages/ProductDetail"));
const Auth = lazy(() => import("@/pages/Auth"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const MyOrders = lazy(() => import("@/pages/MyOrders"));
const Wallet = lazy(() => import("@/pages/Wallet"));
const Unsubscribe = lazy(() => import("@/pages/Unsubscribe"));
const Support = lazy(() => import("@/pages/Support"));
const AdminLayout = lazy(() => import("@/pages/admin/AdminLayout"));
const AdminOverview = lazy(() => import("@/pages/admin/AdminOverview"));
const AdminProducts = lazy(() => import("@/pages/admin/AdminProducts"));
const AdminRanking = lazy(() => import("@/pages/admin/AdminRanking"));
const AdminCategories = lazy(() => import("@/pages/admin/AdminCategories"));
const AdminSales = lazy(() => import("@/pages/admin/AdminSales"));
const AdminWallets = lazy(() => import("@/pages/admin/AdminWallets"));
const AdminReviews = lazy(() => import("@/pages/admin/AdminReviews"));
const AdminPix = lazy(() => import("@/pages/admin/AdminPix"));
const AdminSupport = lazy(() => import("@/pages/admin/AdminSupport"));
const AdminBulkFollows = lazy(() => import("@/pages/admin/AdminBulkFollows"));
const AdminBlog = lazy(() => import("@/pages/admin/AdminBlog"));
const AdminSeo = lazy(() => import("@/pages/admin/AdminSeo"));
const AdminMarketplaceFee = lazy(() => import("@/pages/admin/AdminMarketplaceFee"));
const AdminSuppliers = lazy(() => import("@/pages/admin/AdminSuppliers"));
const AdminProductModeration = lazy(() => import("@/pages/admin/AdminProductModeration"));
const AdminWithdrawals = lazy(() => import("@/pages/admin/AdminWithdrawals"));
const AdminDisputes = lazy(() => import("@/pages/admin/AdminDisputes"));
const AdminReports = lazy(() => import("@/pages/admin/AdminReports"));
const AdminRisk = lazy(() => import("@/pages/admin/AdminRisk"));
const BecomeSupplier = lazy(() => import("@/pages/BecomeSupplier"));
const SupplierStore = lazy(() => import("@/pages/SupplierStore"));
const SupplierLayout = lazy(() => import("@/pages/supplier/SupplierLayout"));
const SupplierDashboard = lazy(() => import("@/pages/SupplierDashboard"));
const SupplierProducts = lazy(() => import("@/pages/supplier/SupplierProducts"));
const SupplierOrders = lazy(() => import("@/pages/supplier/SupplierOrders"));
const SupplierChats = lazy(() => import("@/pages/supplier/SupplierChats"));
const CustomerChats = lazy(() => import("@/pages/CustomerChats"));
const MyDisputes = lazy(() => import("@/pages/MyDisputes"));
const SupplierWithdrawals = lazy(() => import("@/pages/supplier/SupplierWithdrawals"));
const SupplierDisputes = lazy(() => import("@/pages/supplier/SupplierDisputes"));
const SupplierProfile = lazy(() => import("@/pages/supplier/SupplierProfile"));
const SupplierFeatureBids = lazy(() => import("@/pages/supplier/SupplierFeatureBids"));
const SupplierStock = lazy(() => import("@/pages/supplier/SupplierStock"));
const SupplierSummary = lazy(() => import("@/pages/supplier/SupplierSummary"));
const AdminAuditLog = lazy(() => import("@/pages/admin/AdminAuditLog"));
const AdminAntifraud = lazy(() => import("@/pages/admin/AdminAntifraud"));
const AdminConversations = lazy(() => import("@/pages/admin/AdminConversations"));
const SupplierDeposit = lazy(() => import("@/pages/supplier/SupplierDeposit"));
const Tutorials = lazy(() => import("@/pages/Blog"));
const TutorialPost = lazy(() => import("@/pages/BlogPost"));
// DEV-only observability debug harness. Tree-shaken out of production builds
// because the conditional <Route> below evaluates to false when DEV is false.
const ObsDebug = lazy(() => import("@/pages/ObsDebug"));

const ServiceDetail = lazy(() => import("@/pages/ServiceDetail"));
const LandingComprarSeguidores = lazy(() => import("@/pages/landings/ComprarSeguidoresInstagram"));
const LandingCurtidas = lazy(() => import("@/pages/landings/CurtidasInstagram"));
const LandingSeguidoresTiktok = lazy(() => import("@/pages/landings/SeguidoresTiktok"));
const LandingPainelSmm = lazy(() => import("@/pages/landings/PainelSmm"));
const LandingAlternativaBulk = lazy(() => import("@/pages/landings/AlternativaBulkfollows"));
const LandingViewsYoutube = lazy(() => import("@/pages/landings/ComprarVisualizacoesYoutube"));
const LandingInscritosYoutube = lazy(() => import("@/pages/landings/ComprarInscritosYoutube"));
const LandingSeguidoresReais = lazy(() => import("@/pages/landings/SeguidoresReaisInstagram"));
const LandingSeguidoresBarato = lazy(() => import("@/pages/landings/SeguidoresInstagramBarato"));
const LandingImpulsionar = lazy(() => import("@/pages/landings/ImpulsionarInstagram"));
const EnBuyInstagram = lazy(() => import("@/pages/landings/en/BuyInstagramFollowers"));
const EnBuyTiktok = lazy(() => import("@/pages/landings/en/BuyTiktokFollowers"));
const EnSmmPanel = lazy(() => import("@/pages/landings/en/SmmPanel"));
const MyServices = lazy(() => import("@/pages/MyServices"));
const ApiKeys = lazy(() => import("@/pages/ApiKeys"));
const Profile = lazy(() => import("@/pages/Profile"));
const About = lazy(() => import("@/pages/About"));
const Partners = lazy(() => import("@/pages/Partners"));
const Policies = lazy(() => import("@/pages/Policies"));
const PolicyTerms = lazy(() => import("@/pages/policies/PolicyTerms"));
const PolicyPrivacy = lazy(() => import("@/pages/policies/PolicyPrivacy"));
const PolicyDisputes = lazy(() => import("@/pages/policies/PolicyDisputes"));
const PolicyReports = lazy(() => import("@/pages/policies/PolicyReports"));
const PolicyCompliance = lazy(() => import("@/pages/policies/PolicyCompliance"));
// Instagram Verifier was unified into the regular service catalog
// (provider=apify_instagram_check). Old URLs redirect to its service detail page.
const VERIFIER_SERVICE_ID = "322fd800-6463-41d8-95dc-f7a33b878230";
const NotFound = lazy(() => import("./pages/NotFound.tsx"));

const queryClient = new QueryClient();

// PageViewTracker removed in Sprint 2 — AnalyticsProvider handles pageviews.

function DeferredMount({ children }: { children: React.ReactNode }) {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const w: any = window;
    if (typeof w.requestIdleCallback === "function") {
      const id = w.requestIdleCallback(() => setShow(true), { timeout: 3000 });
      return () => w.cancelIdleCallback?.(id);
    }
    const id = setTimeout(() => setShow(true), 1500);
    return () => clearTimeout(id);
  }, []);
  if (!show) return null;
  return <Suspense fallback={null}>{children}</Suspense>;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AppProvider>
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <AuthProvider>
          <AnalyticsProvider>
            <TabSyncBridge />
            <AnalyticsErrorBoundary>
              <Suspense fallback={<RouteLoader />}>
                <Routes>
                  <Route element={<Layout />}>
                    <Route path="/" element={<Home />} />
                    <Route path="/produtos" element={<Products />} />
                    <Route path="/produto/:id" element={<ProductDetail />} />
                    <Route path="/auth" element={<Auth />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/meus-pedidos" element={<MyOrders />} />
                    <Route path="/carteira" element={<Wallet />} />
                    <Route path="/unsubscribe" element={<Unsubscribe />} />
                    <Route path="/email-unsubscribe" element={<Unsubscribe />} />
                    <Route path="/suporte" element={<Support />} />
                    <Route path="/servicos" element={<Services />} />
                    <Route path="/servicos/:id" element={<ServiceDetail />} />
                    <Route path="/meus-servicos" element={<MyServices />} />
                    <Route path="/conta/api" element={<ApiKeys />} />
                    <Route path="/perfil" element={<Profile />} />
                    <Route path="/minhas-mensagens" element={<CustomerChats />} />
                    <Route path="/minhas-disputas" element={<MyDisputes />} />
                    <Route path="/sobre" element={<About />} />
                    <Route path="/parceiros" element={<Partners />} />
                    <Route path="/politicas" element={<Policies />} />
                    <Route path="/politicas/termos" element={<PolicyTerms />} />
                    <Route path="/politicas/privacidade" element={<PolicyPrivacy />} />
                    <Route path="/politicas/disputas" element={<PolicyDisputes />} />
                    <Route path="/politicas/denuncias" element={<PolicyReports />} />
                    <Route path="/politicas/compliance" element={<PolicyCompliance />} />
                    <Route path="/verificador-instagram" element={<Navigate to={`/servicos/${VERIFIER_SERVICE_ID}`} replace />} />
                    <Route path="/instagram-verifier" element={<Navigate to={`/servicos/${VERIFIER_SERVICE_ID}`} replace />} />
                    <Route path="/tutoriais" element={<Tutorials />} />
                    <Route path="/tutoriais/:slug" element={<TutorialPost />} />
                    <Route path="/comprar-seguidores-instagram" element={<LandingComprarSeguidores />} />
                    <Route path="/curtidas-instagram" element={<LandingCurtidas />} />
                    <Route path="/seguidores-tiktok" element={<LandingSeguidoresTiktok />} />
                    <Route path="/painel-smm" element={<LandingPainelSmm />} />
                    <Route path="/alternativa-bulkfollows" element={<LandingAlternativaBulk />} />
                    <Route path="/comprar-visualizacoes-youtube" element={<LandingViewsYoutube />} />
                    <Route path="/comprar-inscritos-youtube" element={<LandingInscritosYoutube />} />
                    <Route path="/seguidores-reais-instagram" element={<LandingSeguidoresReais />} />
                    <Route path="/seguidores-instagram-barato" element={<LandingSeguidoresBarato />} />
                    <Route path="/impulsionar-instagram" element={<LandingImpulsionar />} />
                    <Route path="/en/buy-instagram-followers" element={<EnBuyInstagram />} />
                    <Route path="/en/buy-tiktok-followers" element={<EnBuyTiktok />} />
                    <Route path="/en/smm-panel" element={<EnSmmPanel />} />
                    <Route path="/blog" element={<Navigate to="/tutoriais" replace />} />
                    <Route path="/blog/:slug" element={<TutorialPost />} />
                    <Route path="/seja-supplier" element={<BecomeSupplier />} />
                    <Route path="/loja/:slug" element={<SupplierStore />} />
                    <Route path="/supplier" element={<SupplierLayout />}>
                      <Route index element={<SupplierDashboard />} />
                      <Route path="produtos" element={<SupplierProducts />} />
                      <Route path="estoque" element={<SupplierStock />} />
                      <Route path="resumo" element={<SupplierSummary />} />
                      <Route path="pedidos" element={<SupplierOrders />} />
                      <Route path="chats" element={<SupplierChats />} />
                      <Route path="saques" element={<SupplierWithdrawals />} />
                      <Route path="disputas" element={<SupplierDisputes />} />
                      <Route path="perfil" element={<SupplierProfile />} />
                      <Route path="destaque" element={<SupplierFeatureBids />} />
                      <Route path="caucao" element={<SupplierDeposit />} />
                    </Route>
                    <Route path="/admin" element={<AdminLayout />}>
                      <Route index element={<AdminOverview />} />
                      <Route path="produtos" element={<AdminProducts />} />
                      <Route path="moderacao" element={<AdminProductModeration />} />
                      <Route path="ranking" element={<AdminRanking />} />
                      <Route path="categorias" element={<AdminCategories />} />
                      <Route path="vendas" element={<AdminSales />} />
                      <Route path="carteiras" element={<AdminWallets />} />
                      <Route path="pix" element={<AdminPix />} />
                      <Route path="avaliacoes" element={<AdminReviews />} />
                      <Route path="suporte" element={<AdminSupport />} />
                      <Route path="bulkfollows" element={<AdminBulkFollows />} />
                      <Route path="providers" element={<AdminBulkFollows />} />
                      <Route path="apis" element={<AdminBulkFollows />} />
                      <Route path="blog" element={<AdminBlog />} />
                      <Route path="seo" element={<AdminSeo />} />
                      <Route path="taxa-marketplace" element={<AdminMarketplaceFee />} />
                      <Route path="suppliers" element={<AdminSuppliers />} />
                      <Route path="saques" element={<AdminWithdrawals />} />
                      <Route path="disputas" element={<AdminDisputes />} />
                      <Route path="denuncias" element={<AdminReports />} />
                      <Route path="risco" element={<AdminRisk />} />
                      <Route path="audit" element={<AdminAuditLog />} />
                      <Route path="antifraude" element={<AdminAntifraud />} />
                      <Route path="conversas" element={<AdminConversations />} />
                    </Route>
                    {import.meta.env.DEV && (
                      <Route path="/__obs-debug" element={<ObsDebug />} />
                    )}
                    <Route path="*" element={<NotFound />} />

                  </Route>
                </Routes>
              </Suspense>
            </AnalyticsErrorBoundary>
            <DeferredMount><DeferredOverlays /></DeferredMount>
            <ConsentBanner />
          </AnalyticsProvider>
        </AuthProvider>
      </BrowserRouter>
    </AppProvider>
  </QueryClientProvider>
);

export default App;
